import React, { forwardRef, memo } from 'react';
import { DivWithTextAreaSuggestions } from '../../components/DivWithTextAreaSuggestions/DivWithTextAreaSuggestions';
import { AttachmentCarousel } from '../../components/AttachmentCarousel/AttachmentCarousel';
import type {
  CommentFormProps,
  CommentSubmissionData
} from './CommentForm.types';
import './CommentForm.css';
import { useCommentForm } from './useCommentForm';
import { CommentFormHeader } from './CommentFormHeader';

/**
 * CommentForm - A reusable comment form component using existing Label and DivWithTextAreaSuggestions
 * Optimized for performance and maintainability
 */
const CommentFormComponent = forwardRef<HTMLFormElement, CommentFormProps>(
  (
    {
      labelConfig = {},
      inputConfig,
      onSubmit,
      className,
      style,
      children,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const formResult = useCommentForm({
      labelConfig,
      inputConfig,
      onSubmit,
      className,
      disabled
    });

    const {
      inputProps,
      labelProps,
      formClassName,
      handleSubmit,
      handleKeyDown,
      fileInputRef,
      fileUploadConfig,
      handleFileInputChange,
      attachments,
      previewOpen,
      setPreviewOpen,
      carouselOpen,
      setCarouselOpen,
      carouselIndex,
      uploadAreaRef,
      removeFile,
      openCarousel
    } = formResult;

    const hasFileUpload =
      fileUploadConfig != null &&
      fileInputRef != null &&
      handleFileInputChange != null;

    return (
      <form
        ref={ref}
        className={formClassName}
        style={style}
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {/* Header Label */}
        <CommentFormHeader labelProps={labelProps} />

        {/* Comment Input with Mentions */}
        <div className="comment-form__input-container">
          <DivWithTextAreaSuggestions {...inputProps} />
        </div>

        {hasFileUpload && (
          <>
            {carouselOpen && attachments && attachments.length > 0 && (
              <AttachmentCarousel
                attachments={attachments}
                initialIndex={carouselIndex ?? 0}
                onClose={() => setCarouselOpen?.(false)}
              />
            )}
          </>
        )}

        {/* Additional Content */}
        {children && <div className="comment-form__children">{children}</div>}
      </form>
    );
  }
);

CommentFormComponent.displayName = 'CommentForm';
export const CommentForm = memo(CommentFormComponent);
export default CommentForm;
